// @flow
import React from 'react';
import Toggle from 'react-toggled';
import { withStyles } from '@material-ui/core/styles';
import { withRouter } from 'react-router-dom';

import ListItemIcon from '@material-ui/core/ListItemIcon';
import List from '@material-ui/core/List';
import Link from '@material-ui/core/Link';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import Collapse from '@material-ui/core/Collapse';
import Drawer from '@material-ui/core/Drawer';
import ListItem from '@material-ui/core/ListItem';
import Divider from '@material-ui/core/Divider';
import Help from '@material-ui/icons/Help';
import { Avatar, Menu, MenuItem } from '@material-ui/core';
import ISO6391 from 'iso-639-1';
import capitalize from 'lodash/capitalize';
import { IntercomAPI } from 'react-intercom';
import { withTranslation } from 'react-i18next';
import Language from '@material-ui/icons/Language';

import ScribeFontSizeInputContainer from './ScribeFontSizeInputContainer';
import MicSelectorContainer from './MicSelectorContainer';
import LangSelectorContainer from './LangSelectorContainer';
import ScribeReportModalContainer from './ScribeReportModalContainer';
import ScribeSpeechTrainingSelectorContainer from './ScribeSpeechTrainingSelectorContainer';
import ScribeShortcutModal from './ScribeShortcutModal';
import ScribeSetThemeColorContainer from './ScribeSetThemeColorContainer';
import {
  getDisplayId,
  getDisplayName,
  getAvaName,
  logoutAndClean,
  getUserColor,
} from '../../utils/user';
import EditProfileModal from './EditProfileModal';
import ZoomBroadcastCaptionMenuItem from './ZoomBroadcastCaptionMenuItem';
import { backends, updateBackend, updateBackendAva } from '../../utils/http';
import { SMALL, isMobile, getOS } from '../../utils';
import * as i18nUtils from '../../utils/i18n';
import electron from '../../utils/electron';
import { styles, EmptyIcon } from './SidebarHelpers';
import ConversationModeSelector from './ConversationModeSelector';
import AvaLogoSphere from '../../../assets/images/ava_logo_sphere.png';
import OpenOrDownloadAvaAppComponent from '../../components/OpenOrDownloadAvaAppComponent';

type Props = {
  activeFeatures: Array<string>,
  allowSaving: boolean,
  avaId: string,
  backends: Object,
  classes: Object,
  clearScribeConversationState: Function,
  conversations: Boolean,
  currentPlatform: string,
  displayable: Object,
  features: Object,
  firebaseLogout: Function,
  firebaseUser: Object,
  fullScreen: boolean,
  getTranscriptHtml: Function,
  getTranscriptText: Function,
  history: Object,
  i18n: Function,
  intercomDisplayed: boolean,
  inTwilioCalls: boolean,
  isInConversation: boolean,
  lang: string,
  langs: Array<string>,
  mirror: boolean,
  muted: Boolean,
  reconnectToWebSocket: Function,
  scribe: string,
  scribeDashboard: boolean,
  sidebarShown: boolean,
  status: Object,
  subscription: Subscription,
  switchLang: Function,
  switchMultiEditOnWeb: Function,
  switchPlatform: Function,
  switchWebProduct: Function,
  t: Object,
  theme: Object,
  toggleAudioMode: Function,
  toggleDevMode: Function,
  toggleSideBar: Function,
  updateFullScreen: Function,
  user: Object,
  v1: boolean,
};

type State = {
  devModeComptor: number,
  langAnchorEl: any,
  openOrDownloadAvaApp: boolean,
};
class ScribeSidebar extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      devModeComptor: 0,
      langAnchorEl: null,
      openOrDownloadAvaApp: false,
    };
    this.switchProduct = this.switchProduct.bind(this);
    this.handleMultiEdit = this.handleMultiEdit.bind(this);
    this.switchWebProduct = this.switchWebProduct.bind(this);
  }

  incrementDevModeComptor() {
    const { devModeComptor } = this.state;
    const { toggleDevMode, toggleAudioMode } = this.props;
    if (devModeComptor === 5) {
      toggleDevMode();
    } else if (devModeComptor === 10) {
      this.setState({ devModeComptor: 0 });
      toggleAudioMode();
    }
    this.setState({ devModeComptor: devModeComptor + 1 });
  }

  updateLanguage(lang: string) {
    const { i18n, t } = this.props;
    i18n.changeLanguage(lang, () => {
      window.document.title = t('title');
    });
    this.setState({ langAnchorEl: null });
  }

  handleLangClick = event => {
    this.setState({ langAnchorEl: event.currentTarget });
  };

  switchWebProduct = async (e, product) => {
    e.preventDefault();
    const {
      switchPlatform,
      switchWebProduct,
      history,
      toggleSideBar,
    } = this.props;
    switchWebProduct(product);
    await switchPlatform('web', history);
    toggleSideBar();
  };

  switchProduct = (e: any) => {
    const {
      history,
      switchPlatform,
      currentPlatform,
      toggleSideBar,
    } = this.props;
    e.preventDefault();

    switch (currentPlatform) {
      case 'web':
        switchPlatform('scribe', history).then(() => {
          toggleSideBar();
        });
        break;
      case 'scribe':
        switchPlatform('web', history).then(() => {
          toggleSideBar();
        });
        break;
      default:
        switchPlatform('web', history).then(() => {
          toggleSideBar();
        });
        break;
    }
  };

  handleMultiEdit = e => {
    const { switchMultiEditOnWeb } = this.props;
    e.preventDefault();
    switchMultiEditOnWeb();
  };

  getTranscriptText = e => {
    e.preventDefault();
    const {
      getTranscriptText,
      toggleSideBar,
      getTranscriptHtml,
      scribe,
    } = this.props;
    getTranscriptText();
    if (!SMALL) {
      getTranscriptHtml(scribe);
    }
    toggleSideBar();
  };

  getSubscriptionDisplay = () => {
    let {
      subscription: {
        ongoingSubscription,
      }
    } = this.props;
    const {
      subscription: {
        ongoingSubscriptionSubtype: subType,
      }
    } = this.props;
    // Ava Freedom is a legacy name, which currently confuses users, but is kept
    // in the database to avoid migrating old data.
    // see:
    // https://app.asana.com/0/1152066968391535/1195486190085849/f
    if (ongoingSubscription === 'Freedom') {
      ongoingSubscription = 'Community'
    }
    return `Ava ${ongoingSubscription} ${subType}`;
  };

  updateFullScreen = e => {
    e.preventDefault();
    const { updateFullScreen, fullScreen, toggleSideBar } = this.props;
    // $FlowFixMe
    if (window.navigator.share && document.body.fullScreen)
      // $FlowFixMe
      document.body.requestFullscreen();
    updateFullScreen(!fullScreen);
    toggleSideBar();
  };

  reconnectToWebSocket = () => {
    const { reconnectToWebSocket, toggleSideBar } = this.props;
    reconnectToWebSocket();
    toggleSideBar();
  };

  openOrDownloadAvaApp() {
    // Since it's based on rendering a component,
    // we toggle the visibility alltogether
    this.setState({ openOrDownloadAvaApp: false });
    setTimeout(() => {
      this.setState({ openOrDownloadAvaApp: true });
    }, 0);
  }

  render() {
    const {
      allowSaving,
      avaId,
      backends: backendsAva,
      classes,
      clearScribeConversationState,
      conversations,
      currentPlatform,
      displayable,
      features,
      firebaseLogout,
      firebaseUser,
      history,
      i18n,
      intercomDisplayed,
      inTwilioCalls,
      isInConversation,
      lang,
      langs,
      mirror,
      muted,
      scribe,
      scribeDashboard,
      sidebarShown,
      status,
      subscription,
      switchLang,
      t,
      toggleSideBar,
      user,
      v1,
    } = this.props;

    const { langAnchorEl, openOrDownloadAvaApp } = this.state;

    let marginTop = 67;
    if (window.isElectron) {
      const { remote } = electron;
      if (remote.process.platform === 'darwin') {
        marginTop += 28
      } else {
        marginTop += 32
      }
    }

    const OS = getOS();

    return (
      <Drawer
        classes={{
          paper: classes.drawerPaper,
        }}
        className="invisible-scrollbar"
        open={sidebarShown}
        onClose={toggleSideBar}
        anchor="left"
        variant="persistent"
        style={{
          width: i18n.language === 'fr' ? 300 : null,
        }}
        PaperProps={{
          style: {
            width: i18n.language === 'fr' ? 300 : null,
            height: `calc(100% - ${marginTop}px)`,
            marginTop
          }
        }}
      >
        <List component="nav" disablePadding>
          <ListItem
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'start',
              outline: 'none',
            }}
          >
            <div style={{ flexGrow: 1 }} />
            {firebaseUser && (
              <Avatar
                src={firebaseUser.photoURL}
                alt="profile_photo"
                style={{
                  margin: 'auto',
                  marginRight: 12,
                  width: 36,
                  height: 36,
                  backgroundColor: getUserColor(
                    user && user.userName
                  ),
                }}
              >
                {user &&
                  user.userName &&
                  user.userName[0].toUpperCase()}
              </Avatar>
            )}
            {user && (
              <ListItemText
                className="hover-parent"
                style={{
                  width: '100%',
                }}
              >
                <Toggle>
                  {({ on, getTogglerProps, setOff }) => (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          cursor: currentPlatform === 'web' ? 'pointer' : undefined
                        }}
                        {...(currentPlatform === 'web'
                          ? getTogglerProps()
                          : {})}
                      >
                        <Typography variant="h6" noWrap>
                          {getDisplayName(user)}
                        </Typography>
                      </div>
                      <EditProfileModal open={on} close={setOff} />
                    </div>
                  )}
                </Toggle>
                {getAvaName(user) && (
                  <Typography
                    style={{
                      marginTop: -10,
                    }}
                    noWrap
                    color="primary"
                    variant="subtitle1"
                  >
                    <small><strong>{`&${getAvaName(user)}`}</strong></small>
                  </Typography>
                )}
                <Typography
                  style={{
                    marginTop: -10,
                  }}
                  noWrap
                  color="primary"
                  variant="subtitle1"
                >
                  <small>{getDisplayId(user, t)}</small>
                </Typography>
                {subscription && subscription.ongoingSubscription && (
                  <Typography
                    style={{ marginTop: -10 }}
                    variant="subtitle1"
                    noWrap>
                    <small>
                      {this.getSubscriptionDisplay()}
                    </small>
                  </Typography>
                )}
                {user &&
                  user.profile &&
                  user.profile.orgProperties &&
                  user.profile.orgProperties.role === "staff" && (
                  <Typography
                    style={{
                      marginTop: -10,
                    }}
                    noWrap
                    color="secondary"
                    variant="subtitle1"
                  >
                    <small>
                      <Link
                        style={{
                          color: "#777",
                          fontWeight: 600,
                        }}
                        target="_blank"
                        rel="noreferrer noopener"
                        href="/account/dashboard"
                      >
                        {t("sidebar.adminDashboard")}
                        <i
                          style={{
                            fontSize: '1em',
                            transform: 'translate(0.2em, 0.16em)',
                          }}
                          className="material-icons">
                          open_in_new
                        </i>
                      </Link>
                    </small>
                  </Typography>
                )}
              </ListItemText>
            )}
          </ListItem>
          {currentPlatform === 'web' &&
            ((!v1 && displayable.v1) ||
            (!conversations && displayable.conversations) ||
            (!scribeDashboard && displayable.scribeDashboard)) && <Divider />}
          {currentPlatform === 'web' && (
            <>
              {!v1 && displayable.v1 && (
                <ListItem
                  key="scribe_v1_toggle"
                  onClick={e => this.switchWebProduct(e, 'v1')}
                  role="button"
                  button
                >
                  <ListItemIcon>
                    <i className="material-icons">call_made</i>
                  </ListItemIcon>
                  <ListItemText
                    classes={{ root: classes.listItemText }}
                    primary={t("sidebar.joinAConversation")}
                  />
                </ListItem>
              )}
              {!conversations && displayable.conversations && (
                <ListItem
                  key="onversations_"
                  onClick={e => this.switchWebProduct(e, 'conversations')}
                  role="button"
                  button
                >
                  <ListItemIcon>
                    <i className="material-icons">list_alt</i>
                  </ListItemIcon>
                  <ListItemText
                    classes={{ root: classes.listItemText }}
                    primary={t("sidebar.conversations")}
                  />
                </ListItem>
              )}
              {!scribeDashboard && displayable.scribeDashboard && (
                <ListItem
                  key="scribe_dashboard_toggle"
                  onClick={e => this.switchWebProduct(e, 'scribeDashboard')}
                  role="button"
                  button
                >
                  <ListItemIcon>
                    <i className="material-icons">view_agenda</i>
                  </ListItemIcon>
                  <ListItemText
                    classes={{ root: classes.listItemText }}
                    primary={t("sidebar.scribeDashboard")}
                  />
                </ListItem>
              )}
            </>
          )}
          {features.scribe &&
            (displayable.rv || currentPlatform === 'scribe') &&
            features['ava-webapp'] && [
              currentPlatform === 'scribe' && <Divider key="divider1" />,
              <ListItem
                key="scribe_toggle"
                onClick={this.switchProduct}
                role="button"
                button
              >
                <EmptyIcon />
                <ListItemText
                  classes={{ root: classes.listItemText }}
                  primary={
                    currentPlatform === 'web'
                      ? 'Scribe FFT (RogerVoice)'
                      : 'Ava web'
                  }
                />
              </ListItem>,
            ]}
          {(isInConversation ||
            displayable.mirror ||
            displayable.conversations ||
            displayable.scribeDashboard) && <Divider />}
          <Toggle key="0_toggle_array" defaultOn>
            {({ on, getTogglerProps }) => [
              !window.isElectron && (
                <>
                  <ListItem
                    button
                    key="app-bridge"
                    classes={{
                      focused: classes.root,
                    }}
                    onClick={() => this.openOrDownloadAvaApp()}
                  >
                    <ListItemIcon>
                      <img
                        src={AvaLogoSphere}
                        style={{ height: 24 }}
                        alt="Ava logo"
                      />
                    </ListItemIcon>
                    <ListItemText
                      classes={{ root: classes.listItemText }}
                      primary={
                        isMobile()
                          ? t('sidebar.openAva')
                          : t('sidebar.openAvaDesktop', { OS })
                      }
                    />
                  </ListItem>
                  <Divider />
                </>
              ),
              <ListItem
                button
                {
                  ...getTogglerProps()
                }
                key="toggle_head"
                classes={{
                  'focused': classes.root
                }}>
                <ListItemIcon>
                  <i className="material-icons">subtitles</i>
                </ListItemIcon>
                <ListItemText
                  classes={{ root: classes.listItemText }}
                  primary={t('sidebar.captionSettings')}
                />
                <ListItemIcon classes={{ root: classes.expandIcon }}>
                  <i className="material-icons">
                    {on ? 'expand_less' : 'expand_more'}
                  </i>
                </ListItemIcon>
              </ListItem>,
              <Collapse in={on} timeout="auto" unmountOnExit key="toggle_collapse">
                {isInConversation && <ScribeFontSizeInputContainer />}
                <ScribeSetThemeColorContainer />
                <ListItem
                  onClick={this.updateFullScreen}
                  style={{
                    paddingLeft: 32,
                  }}
                  role="button"
                  button>
                  <ListItemIcon>
                    <i className="material-icons">fullscreen</i>
                  </ListItemIcon>
                  <ListItemText
                    classes={{ root: classes.listItemText }}
                    primary={t('theme.fullScreen')}
                  />
                </ListItem>
                {isInConversation &&
                  user &&
                  user.features &&
                  user.features['zoom-caption'] && <ZoomBroadcastCaptionMenuItem />}
              </Collapse>
            ]}
          </Toggle>
          {isInConversation && <ConversationModeSelector status={status} avaId={avaId} subscription={subscription} mode={status.conversationMode} />}
          {isInConversation && displayable.audio && <LangSelectorContainer />}
          {isInConversation && (
            <Toggle>
              {({ on, getTogglerProps }) => (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {langs.length > 2 && (
                    <div>
                      <ListItem button {...getTogglerProps()}>
                        <ListItemIcon>
                          <i className="material-icons">language</i>
                        </ListItemIcon>
                        <ListItemText classes={{ root: classes.listItemText }}>
                          {t('sidebar.switchLang')}
                          <br />
                          <b>
                            {lang === '~'
                              ? t('sidebar.originalVersion')
                              : ISO6391.getNativeName(lang)}
                          </b>
                        </ListItemText>
                        <ListItemIcon classes={{ root: classes.expandIcon }}>
                          <i className="material-icons">
                            {on ? 'expand_less' : 'expand_more'}
                          </i>
                        </ListItemIcon>
                      </ListItem>
                      <Collapse in={on} timeout="auto" unmountOnExit>
                        <List>
                          {langs.map((lang_: string) => (
                            <ListItem
                              key={lang_}
                              button
                              style={{
                                display: lang === lang_ ? 'none' : undefined,
                              }}
                            >
                              <EmptyIcon />
                              <ListItemText
                                classes={{ root: classes.listItemText }}
                                onClick={() => switchLang(lang_)}
                                primary={`${
                                  lang_ === '~'
                                    ? t('sidebar.originalVersion')
                                    : ISO6391.getNativeName(lang_)
                                }`}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Collapse>
                    </div>
                  )}
                </div>
              )}
            </Toggle>
          )}
          {currentPlatform === 'web' &&
            !muted &&
            !scribe &&
            isInConversation &&
            !inTwilioCalls &&
            displayable.audio && (
            <MicSelectorContainer />
          )}
          {currentPlatform === 'scribe' && (
            <ScribeSpeechTrainingSelectorContainer classes={classes} />
          )}
          {(currentPlatform === 'scribe' || features.scribe) && <Divider />}
          {(currentPlatform === 'scribe' || features.scribe) && [
            <Toggle key="1_toggle_array">
              {({ on, getTogglerProps, setOff }) => (
                <ListItem button>
                  <ListItemIcon>
                    <i className="material-icons">report</i>
                  </ListItemIcon>
                  <ListItemText
                    classes={{ root: classes.listItemText }}
                    {...getTogglerProps()}
                    primary={t("sidebar.reportIssue")}
                  />
                  <ScribeReportModalContainer
                    open={on}
                    close={setOff}
                    old={currentPlatform === 'scribe'}
                  />
                </ListItem>
              )}
            </Toggle>,
          ]}
          {features.scribe && (
            <Toggle key="2_toggle_array">
              {({
                on: on2,
                getTogglerProps: getTogglerProps2,
                setOff: setOff2,
              }) => (
                <ListItem button>
                  <ListItemIcon>
                    <i className="material-icons">keyboard</i>
                  </ListItemIcon>
                  <ListItemText
                    classes={{ root: classes.listItemText }}
                    {...getTogglerProps2()}
                    primary={t("sidebar.viewShortcutList")}
                  />
                  <ScribeShortcutModal open={on2} close={setOff2} />
                </ListItem>
              )}
            </Toggle>
          )}
          {features.scribe && (
            <ListItem
              button
              onClick={() => {
                window.open(
                  'https://www.notion.so/ava/Scribe-guide-fr-translated-en-7f83c1e0158b4df3b9a5dbbeca3143f0',
                  '_blank'
                );
              }}
            >
              <ListItemIcon>
                <i className="material-icons">book</i>
              </ListItemIcon>
              <ListItemText
                classes={{ root: classes.listItemText }}
                primary={t("sidebar.scribeUserGuide")}
              />
            </ListItem>
          )}
          {currentPlatform === 'web' &&
            (isInConversation || mirror) &&
            allowSaving && [
              <ListItem
                key="download_text"
                onClick={this.getTranscriptText}
                role="button"
                button
              >
                <ListItemIcon>
                  <i className="material-icons">save_alt</i>
                </ListItemIcon>
                <ListItemText
                  classes={{ root: classes.listItemText }}
                  primary={t('sidebar.downloadText')}
                />
              </ListItem>,
            ]
          }
          {!intercomDisplayed && (
            <ListItem button>
              <ListItemIcon>
                <i className="material-icons">
                  <Help />
                </i>
              </ListItemIcon>
              <ListItemText
                classes={{ root: classes.listItemText }}
                primary={t('sidebar.contactSupport')}
                onClick={() => {
                  IntercomAPI('show');
                  toggleSideBar();
                }}
              />
            </ListItem>
          )}
          {displayable.dev && <Divider />}
          {displayable.dev && (
            <Toggle>
              {({ on, getTogglerProps }) => (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <ListItem button {...getTogglerProps()}>
                    <ListItemIcon>
                      <i className="material-icons">cloud</i>
                    </ListItemIcon>
                    <ListItemText
                      classes={{ root: classes.listItemText }}
                      primary={t('sidebar.serverSettings')}
                    />
                    <ListItemIcon classes={{ root: classes.expandIcon }}>
                      <i className="material-icons">
                        {on ? 'expand_less' : 'expand_more'}
                      </i>
                    </ListItemIcon>
                  </ListItem>
                  <Collapse in={on} timeout="auto" unmountOnExit>
                    <List>
                      {currentPlatform === 'scribe' &&
                        Object.keys(backends).map(backendId => (
                          <ListItem
                            key={backendId}
                            button
                            style={{
                              display:
                                localStorage.getItem('scribeBackendUrl') ===
                                backends[backendId].url
                                  ? 'none'
                                  : undefined,
                            }}
                          >
                            <EmptyIcon />
                            <ListItemText
                              classes={{ root: classes.listItemText }}
                              onClick={() => updateBackend(backendId)}
                              primary={`Use ${backendId} Scribe backend`}
                            />
                          </ListItem>
                        ))}
                      {currentPlatform === 'web' &&
                        Object.keys(backendsAva).map(backendId => (
                          <ListItem
                            key={backendId}
                            button
                            style={{
                              display:
                                localStorage.getItem('avaBackendUrl') ===
                                backendsAva[backendId].url
                                  ? 'none'
                                  : undefined,
                            }}
                          >
                            <EmptyIcon />
                            <ListItemText
                              classes={{ root: classes.listItemText }}
                              onClick={() =>
                                updateBackendAva(backendId, backendsAva)
                              }
                              primary={
                                backendsAva[backendId].name
                                  ? backendsAva[backendId].name
                                  : capitalize(backendId)
                              }
                            />
                          </ListItem>
                        ))}
                      {currentPlatform === 'web' &&
                        displayable.dev && [
                          <ListItem
                            key="reconnect"
                            onClick={() => this.reconnectToWebSocket()}
                            role="button"
                            button
                          >
                            <ListItemIcon>
                              <i className="material-icons">cached</i>
                            </ListItemIcon>
                            <ListItemText
                              classes={{ root: classes.listItemText }}
                              primary={t('sidebar.reconnectToBackend')}
                            />
                          </ListItem>,
                        ]}
                    </List>
                  </Collapse>
                </div>
              )}
            </Toggle>
          )}
          <Divider />
          <ListItem
            button
            onClick={async () => {
              await logoutAndClean();
              clearScribeConversationState();
              firebaseLogout();
              history.push((firebaseUser || {}).isAnonymous ? '/scribe/login' : '/scribe');
              window.history.pushState({}, document.title, (firebaseUser || {}).isAnonymous ? '/#/scribe/login' : '/#/scribe');
              window.location.reload();
            }}>
            <ListItemIcon>
              <i className="material-icons">exit_to_app</i>
            </ListItemIcon>
            <ListItemText
              classes={{ root: classes.listItemText }}
              primary={
                (firebaseUser || {}).isAnonymous
                  ? t('sidebar.signIn')
                  : t('sidebar.signOut')
              }
            />
          </ListItem>
          <ListItem>
            {displayable.dev && (
              <ListItemText>
                <Typography
                  variant="subtitle1"
                  color="primary"
                  onClick={() => this.incrementDevModeComptor()}
                >
                  <small>
                    {String(process.env.TAG_COMMIT)}
                  </small>
                </Typography>
              </ListItemText>
            )}
            <button
              style={{
                display: 'flex',
                flexDirection: 'row',
                position: 'absolute',
                right: 10,
              }}
              onClick={this.handleLangClick}
              className="a-button"
              type="submit"
            >
              <Language
                style={{
                  width: 16,
                  height: 16,
                  marginTop: 8,
                  marginRight: 5,
                  color: '#777',
                }}
              />
              <Typography
                aria-owns={langAnchorEl ? 'simple-menu' : undefined}
                color="primary"
                aria-haspopup="true"
                variant="subtitle1"
              >
                <small>{t('sidebar.language')}</small>
              </Typography>
            </button>
            <Menu
              id="simple-menu"
              anchorEl={langAnchorEl}
              open={Boolean(langAnchorEl)}
            >
              {i18nUtils.langs.map(lg => (
                <MenuItem
                  key={lg}
                  onClick={() => this.updateLanguage(lg)}
                  selected={lg === i18n.language}
                >
                  {i18nUtils.langsDisplay[lg]}
                </MenuItem>
              ))}
            </Menu>
          </ListItem>
        </List>
        {openOrDownloadAvaApp && (
          <OpenOrDownloadAvaAppComponent />
        )}
      </Drawer>
    );
  }
}
export default withTranslation()(
  withStyles(styles, { withTheme: true })(withRouter(ScribeSidebar))
);
